import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { PanelSkeleton } from '../panelSkeleton/PanelSkeleton';
import { SectionCard } from '../sectionCard/SectionCard';
import * as PanelStyled from '../timeSeriesPanel/TimeSeriesPanel.styles';

import { BarPanelProps } from './BarPanel.types';

/**
 * Bar panel used for categorical breakdowns (DLQ per reason, top errors, retries per command).
 *
 * Clicking a bar calls `onSelect` with the datum id, which is how the design turns these charts
 * into filter controls rather than decoration.
 */
export const BarPanel = ({
  title,
  data,
  layout = 'vertical',
  height = 240,
  onSelect,
  selectedId,
  isLoading = false,
  error,
  grafanaUrl,
  className,
}: BarPanelProps): JSX.Element => {
  const { formatMessage } = useLocale();
  const { palette } = useTheme();

  const colors = useMemo(
    () =>
      data.map((datum, index) => {
        const base = datum.color ?? palette.chart.series[index % palette.chart.series.length];

        return selectedId && selectedId !== datum.id ? palette.chart.neutral : base;
      }),
    [data, palette, selectedId],
  );

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return <PanelSkeleton height={height} withTitle={false} />;
    }

    if (error) {
      return (
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.common.error'] })} />
        </div>
      );
    }

    if (data.length === 0) {
      return <NothingToShow text={formatMessage({ id: AppMessages['dod.common.empty'] })} />;
    }

    const categoryAxis = { scaleType: 'band' as const, data: data.map((datum) => datum.label) };
    const valueAxis = {};

    return (
      <BarChart
        height={height}
        layout={layout}
        xAxis={layout === 'horizontal' ? [valueAxis] : [categoryAxis]}
        yAxis={layout === 'horizontal' ? [categoryAxis] : [valueAxis]}
        series={[{ data: data.map((datum) => datum.value) }]}
        colors={colors}
        margin={{ top: 12, right: 16, bottom: 24, left: layout === 'horizontal' ? 120 : 48 }}
        slotProps={{ legend: { hidden: true } }}
        sx={{
          '& .MuiChartsGrid-line': { stroke: palette.chart.grid },
          '& .MuiBarElement-root': { cursor: onSelect ? 'pointer' : 'default' },
        }}
        onItemClick={onSelect ? (_event, item) => data[item.dataIndex] && onSelect(data[item.dataIndex].id) : undefined}
      />
    );
  };

  return (
    <SectionCard
      className={className}
      title={title}
      bodyHeight={height}
      actions={
        grafanaUrl ? (
          <PanelStyled.GrafanaLink href={grafanaUrl} target="_blank" rel="noreferrer noopener">
            {formatMessage({ id: AppMessages['dod.common.openInGrafana'] })}
          </PanelStyled.GrafanaLink>
        ) : undefined
      }
    >
      {renderBody()}
    </SectionCard>
  );
};
